// zxing-wasm is pulled as an IIFE bundle from a CDN instead of being imported from npm: webpack 4 cannot parse the
// `import.meta` its ESM build relies on to locate the wasm. Loading the decoder from a CDN keeps the build
// configuration untouched.
const ZXING_BASE = 'https://cdn.jsdelivr.net/npm/zxing-wasm@3.1.4'
const SCRIPT_URL = `${ZXING_BASE}/dist/iife/reader/index.js`
// The bundle hardcodes fastly.jsdelivr.net for its own wasm, a different host from the one serving the script. On iOS
// that second host proved unreachable while the script loaded, aborting the module on an opaque Emscripten message.
// Fetching the binary ourselves keeps every request on a single host and surfaces a readable HTTP status on failure.
const WASM_URL = `${ZXING_BASE}/dist/reader/zxing_reader.wasm`

const SCRIPT_TIMEOUT_MS = 10000
const WARM_UP_SIZE = 64

// `formats` is deliberately omitted: that is how zxing-wasm is told to try every symbology it knows
export const READER_OPTIONS = {
    maxNumberOfSymbols: 1,
    tryHarder: true,
    tryRotate: true,
    tryInvert: true,
    // The capture already hands over a native-resolution crop; an internal downscale destroys dense Code 128 bars
    tryDownscale: false,
}

// The short code travels beside the message: it is what reaches the operator's screen, so it has to stay readable
// over the phone to support while the message itself keeps the full technical trail for the console
function readerFailure(message, detail) {
    const error = new Error(message)
    error.detail = detail

    return error
}

let script_promise = null
let decoder_promise = null

function loadScript() {
    if (!script_promise) {
        script_promise = new Promise((resolve, reject) => {
            const script = document.createElement('script')

            const timeout_id = setTimeout(() => {
                // Left in place, the tag would still load and a retry would evaluate the bundle a second time
                script.remove()
                script_promise = null
                reject(readerFailure(`Timed out after ${SCRIPT_TIMEOUT_MS} ms loading ${SCRIPT_URL}`, 'TIMEOUT'))
            }, SCRIPT_TIMEOUT_MS)

            script.src = SCRIPT_URL
            script.async = true
            script.onload = () => {
                clearTimeout(timeout_id)
                resolve()
            }
            script.onerror = () => {
                clearTimeout(timeout_id)
                script.remove()
                script_promise = null
                reject(readerFailure(`Unable to load ${SCRIPT_URL}`, 'NETWORK'))
            }

            document.head.appendChild(script)
        })
    }

    return script_promise
}

async function loadWasmBinary() {
    let response = null

    try {
        response = await fetch(WASM_URL)
    } catch (error) {
        throw readerFailure(`Unable to reach ${WASM_URL}`, 'NETWORK')
    }

    if (!response.ok) {
        throw readerFailure(`Unable to download the wasm (HTTP ${response.status}) from ${WASM_URL}`, `HTTP ${response.status}`)
    }

    return response.arrayBuffer()
}

async function initialise() {
    await loadScript()
    await window.ZXingWASM.prepareZXingModule({ overrides: { wasmBinary: await loadWasmBinary() }, fireImmediately: true })

    // One blank decode so the first real frame is not charged with the module warm-up
    const blank_frame = new Uint8ClampedArray(WARM_UP_SIZE * WARM_UP_SIZE * 4).fill(255)
    await window.ZXingWASM.readBarcodes(new ImageData(blank_frame, WARM_UP_SIZE, WARM_UP_SIZE), READER_OPTIONS)

    return window.ZXingWASM
}

// The wasm module is instantiated once for the whole application
export function getDecoder() {
    if (!decoder_promise) {
        decoder_promise = initialise().catch((error) => {
            // Cleared so that retrying does not replay the rejected promise forever
            decoder_promise = null
            throw error
        })
    }

    return decoder_promise
}
